/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useMemo, useState } from "react";
import {
  Typography,
  Box,
  Tabs,
  Tab,
  ButtonBase,
  Popover,
  Autocomplete,
  TextField,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PropTypes from "prop-types";
import Clock from "react-live-clock";
import ViewDayOutlined from "@mui/icons-material/ViewDayOutlined";
import LineAxisOutlined from "@mui/icons-material/LineAxisOutlined";
import TroubleshootOutlined from "@mui/icons-material/TroubleshootOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import FeaturedPlayListOutlined from "@mui/icons-material/FeaturedPlayListOutlined";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import { getTimezoneAbbreviation } from "../../lib/Util";
import { useQuery } from "@tanstack/react-query";
import { getCurrentWeatherQuery } from "../../lib/Queries";
import { CircularProgress } from "@mui/material";
import WeatherDisplay from "../Shared/WeatherDisplay";
import { useNavigate } from "react-router-dom";
import useUserStore from "./../../state/UserStore";

const SiteHeader = ({ metadata, setActive, hasRecentAutoReport, sites, siteValid }) => {
  const [activeTab, setActiveTab] = useState("summary");
  const [siteMenuAnchor, setSiteMenuAnchor] = useState(null);
  const [siteSearch, setSiteSearch] = useState("");
  const navigate = useNavigate();
  const userStore = useUserStore((state) => state);

  const operatingSites = useMemo(
    () =>
      (sites || [])
        .filter((site) => site?.state === "Operating")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [sites],
  );

  const handleSiteMenuOpen = (ev) => {
    setSiteSearch("");
    setSiteMenuAnchor(ev.currentTarget);
  };

  const handleSiteMenuClose = () => {
    setSiteMenuAnchor(null);
    setSiteSearch("");
  };

  const handleSiteSelect = (site) => {
    if (!site) {
      return;
    }
    handleSiteMenuClose();
    if (site.name === metadata.site_name) {
      return;
    }
    localStorage.setItem("site", site.uuid);
    navigate(`/site/${site.name.replace(/ /g, "-")}`);
  };
  const { data: weatherData, isLoading: weatherLoading } = useQuery({
    ...getCurrentWeatherQuery(metadata?.uuid),
    enabled: !!metadata?.uuid && siteValid,
  });

  const handleChange = (ev, tab) => {
    setActiveTab(tab);
    setActive(tab);
  };

  if (!metadata) {
    return null;
  }

  const getWeather = () => {
    const getDivider = () => (
      <Box ml="0.5em" mr="0.5em">
        <Typography variant="body2" color="grey">
          |
        </Typography>
      </Box>
    );

    const getWeatherContent = () => (
      <Box sx={{ display: "flex" }}>
        <WeatherDisplay weatherData={weatherData?.current} />
      </Box>
    );

    const getWeatherLoading = () => (
      <>
        <CircularProgress size={20} />
      </>
    );

    return (
      <>
        {weatherLoading ? getWeatherLoading() : getWeatherContent()}
        {getDivider()}
      </>
    );
  };

  const handleClick = () => {
    const orgNameFormatted = metadata.customer_name.replace(/ /g, "-");
    const siteNameFormatted = metadata.site_name.replace(/ /g, "-");
    navigate(`/organization/${orgNameFormatted}/sites/${siteNameFormatted}`);
  };

  return (
    <Box>
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
        <Box sx={{ width: "80%" }}>
          <ButtonBase
            id="site-switcher-button"
            onClick={handleSiteMenuOpen}
            disabled={operatingSites.length === 0}
            disableRipple
            aria-haspopup="listbox"
            aria-expanded={Boolean(siteMenuAnchor)}
            aria-controls={siteMenuAnchor ? "site-switcher-popover" : undefined}
            aria-label="Switch site"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              cursor: operatingSites.length > 0 ? "pointer" : "default",
            }}
          >
            <Typography
              className="dashboard-site"
              variant="h6"
              component="div"
              sx={{ width: "auto" }}
            >
              {metadata.site_name}
            </Typography>
            {operatingSites.length > 0 && (
              <ArrowDropDownIcon
                sx={{
                  transition: "transform 0.15s ease",
                  transform: siteMenuAnchor ? "rotate(180deg)" : "none",
                }}
              />
            )}
          </ButtonBase>
          <Popover
            id="site-switcher-popover"
            anchorEl={siteMenuAnchor}
            open={Boolean(siteMenuAnchor)}
            onClose={handleSiteMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Box sx={{ width: 280, p: 1 }}>
              <Autocomplete
                open
                openOnFocus
                autoHighlight
                size="small"
                options={operatingSites}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                value={null}
                inputValue={siteSearch}
                onInputChange={(event, newInputValue) => setSiteSearch(newInputValue)}
                onChange={(event, newValue) => handleSiteSelect(newValue)}
                noOptionsText="No operating sites"
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={option.uuid}
                    sx={{ fontWeight: option.name === metadata.site_name ? 600 : 400 }}
                  >
                    {option.name}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} autoFocus placeholder="Search sites" variant="outlined" />
                )}
                slotProps={{
                  paper: { sx: { boxShadow: "none", m: 0 } },
                  popper: {
                    disablePortal: true,
                    sx: {
                      position: "static !important",
                      transform: "none !important",
                      width: "100%",
                    },
                  },
                  listbox: { sx: { maxHeight: 320 } },
                }}
              />
            </Box>
          </Popover>
        </Box>
        <Box flex={1} display="flex" flexDirection="row" justifyContent="space-between">
          <Tabs
            orientation="horizontal"
            value={activeTab}
            onChange={handleChange}
            aria-label="site options nav"
            sx={{
              "& .MuiTabs-flexContainerVertical": {
                alignItems: "center",
              },
              ".MuiTab-root": {
                color: "#7f99c2",
              },
            }}
            TabIndicatorProps={{
              style: { background: "#7f99c2" },
            }}
            scrollButtons={false}
            centered={true}
          >
            <Tab
              disableRipple={true}
              icon={<ViewDayOutlined fontSize="small" />}
              value="summary"
              sx={{ minWidth: 0 }}
              label="Overview"
              iconPosition="start"
            />
            <Tab
              disableRipple={true}
              icon={<LineAxisOutlined fontSize="small" />}
              value="detail"
              sx={{ minWidth: 0 }}
              label="Grapher"
              iconPosition="start"
            />
            <Tab
              disableRipple={true}
              icon={<TroubleshootOutlined fontSize="small" />}
              value="activity"
              sx={{ minWidth: 0 }}
              label="Activity"
              iconPosition="start"
            />
            <Tab
              disableRipple={true}
              icon={<FeaturedPlayListOutlined fontSize="small" />}
              value="specs"
              sx={{ minWidth: 0 }}
              label="Specs"
              iconPosition="start"
            />
            <Tab
              disableRipple={true}
              icon={<ReceiptLongOutlined fontSize="small" />}
              value="performance"
              sx={{ minWidth: 0 }}
              label={
                hasRecentAutoReport ? (
                  <Box component="span">
                    Performance{" "}
                    <Box component="span" sx={{ color: "secondary.main", fontSize: "1.2em" }}>
                      &bull;
                    </Box>
                  </Box>
                ) : (
                  "Performance"
                )
              }
              iconPosition="start"
            />
          </Tabs>
        </Box>
      </Box>
      <div className="line"></div>
      <Box width="100%" display="flex" flexDirection="row" justifyContent="space-between">
        <Box display="flex" flexDirection="row">
          {metadata.address ? (
            <>
              <Box>
                <Typography variant="body2" color="secondary">
                  {metadata.address.address1}, {metadata.address.city}, {metadata.address.state}{" "}
                  {metadata.address.zip}
                </Typography>
              </Box>
              {metadata.latitude && (
                <Box ml="0.5em" mr="0.5em">
                  <Typography variant="body2" color="grey">
                    |
                  </Typography>
                </Box>
              )}
            </>
          ) : null}
          {metadata.latitude && metadata.longitude ? (
            <Box>
              <a
                href={
                  "https://www.google.com/maps/search/?api=1&query=" +
                  metadata.latitude +
                  "," +
                  metadata.longitude
                }
                target="_new"
              >
                <Typography
                  variant="body2"
                  color="secondary"
                  style={{ textDecoration: "underline" }}
                >
                  {metadata.latitude},{metadata.longitude}
                </Typography>
              </a>
            </Box>
          ) : null}
        </Box>
        <Box display="flex" flexDirection="row">
          <Box mr="0.25em">
            <Typography variant="body2" color="grey">
              Site time
            </Typography>
          </Box>
          <Box mr="0.25em">
            <Typography variant="body2" color="secondary">
              <Clock format={"HH:mm:ss"} ticking={true} timezone={metadata.timezone} />
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="secondary">
              {getTimezoneAbbreviation(metadata?.timezone)}
            </Typography>
          </Box>
          <Box ml="0.5em" mr="0.5em">
            <Typography variant="body2" color="grey">
              |
            </Typography>
          </Box>
          {siteValid ? getWeather() : null}
          <Box>
            <Typography variant="body2" color="secondary">
              {metadata?.state}
            </Typography>
          </Box>

          {userStore.isAdmin() && (
            <>
              <Box ml="0.5em" mr="0.5em">
                <Typography variant="body2" color="grey">
                  |
                </Typography>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                onClick={handleClick}
                sx={{ cursor: "pointer" }}
              >
                <SettingsOutlinedIcon fontSize="small" color="secondary" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="secondary">
                  Site Settings
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

SiteHeader.propTypes = {
  metadata: PropTypes.object,
  setActive: PropTypes.func,
  hasRecentAutoReport: PropTypes.bool,
  sites: PropTypes.array,
  siteValid: PropTypes.bool,
};

export default SiteHeader;
